/**
 * React hooks for data fetching with React Query
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import type { TeamMember, CapacityMetrics, Sprint, TimelineAllocation, ApiResponse } from '../types';

/**
 * Fetch team members
 */
export function useTeamData(page: number = 1): UseQueryResult<any, unknown> {
  return useQuery({
    queryKey: ['team', page],
    queryFn: () => apiClient.getTeam(page),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2,
  });
}

/**
 * Fetch capacity metrics for a sprint
 */
export function useCapacity(sprintId?: string, userId?: string): UseQueryResult<ApiResponse<CapacityMetrics>, unknown> {
  return useQuery({
    queryKey: ['capacity', sprintId, userId],
    queryFn: () => apiClient.getCapacity(sprintId, userId),
    enabled: !!sprintId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2,
  });
}

/**
 * Fetch sprints
 */
export function useSprints(status?: string): UseQueryResult<ApiResponse<Sprint[]>, unknown> {
  return useQuery({
    queryKey: ['sprints', status],
    queryFn: () => apiClient.getSprints(status),
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: 2,
  });
}

/**
 * Fetch single sprint
 */
export function useSprint(sprintId?: string): UseQueryResult<ApiResponse<Sprint>, unknown> {
  return useQuery({
    queryKey: ['sprint', sprintId],
    queryFn: () => apiClient.getSprint(sprintId!),
    enabled: !!sprintId,
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: 2,
  });
}

/**
 * Fetch allocations timeline
 */
export function useAllocations(
  startDate?: Date,
  endDate?: Date,
  userId?: string
): UseQueryResult<ApiResponse<TimelineAllocation[]>, unknown> {
  return useQuery({
    queryKey: ['allocations', startDate?.toISOString(), endDate?.toISOString(), userId],
    queryFn: () => {
      if (!startDate || !endDate) {
        return Promise.reject(new Error('Start and end dates required'));
      }
      return apiClient.getAllocations(startDate, endDate, userId);
    },
    enabled: !!startDate && !!endDate,
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: 2,
  });
}

/**
 * Fetch configuration
 */
export function useConfig(): UseQueryResult<ApiResponse<any>, unknown> {
  return useQuery({
    queryKey: ['config'],
    queryFn: () => apiClient.getConfig(),
    staleTime: 1000 * 60 * 30, // 30 minutes
    retry: 2,
  });
}

/**
 * Check API health
 */
export function useHealth(): UseQueryResult<boolean, unknown> {
  return useQuery({
    queryKey: ['health'],
    queryFn: () => apiClient.health(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
  });
}
